import { isReference } from '@apollo/client';
import type {
  FetchResult,
  StoreObject,
  WatchQueryFetchPolicy,
} from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { GetFhirRecordParams } from '@models/fhir-resource';
import { MedicationStatementRecord } from '@models/medication';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useGetMedicationStatementQuery,
  useListMedicationStatementsQuery,
  useUpdateMedicationStatementRecordMutation,
  usePostMedicationStatementRecordMutation,
  useDeleteMedicationStatementRecordMutation,
} from 'generated/medication.generated';
import type {
  PostMedicationStatementRecordMutationVariables,
  UpdateMedicationStatementRecordMutationVariables,
  DeleteMedicationStatementRecordMutation,
  DeleteMedicationStatementRecordMutationVariables,
} from 'generated/medication.generated';

export const useGetMedicationStatements = (options?: {
  /**
   * Defaults to Apollo's normal cache-first behaviour. Pass `'no-cache'` for
   * flows (like PDF export) that must not silently reuse a stale result
   * already sitting in the cache from a previous visit to the list page.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
}) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListMedicationStatementsQuery({
    variables: {
      input: { patient: patient_id, count: 1500, page: 1 },
    },
    skip: !patient_id,
    fetchPolicy: options?.fetchPolicy,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listMedicationStatements.data ?? []).map(
    (r) => new MedicationStatementRecord(r)
  );
  return { ...response, data: records };
};

export const useGetMedicationStatement = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetMedicationStatementQuery({
    variables: { input: { ...params, patient_id } },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getMedicationStatement;
  return {
    ...response,
    data: record ? new MedicationStatementRecord(record) : null,
  };
};

export function useCreateMedication(): [
  (
    params: PostMedicationStatementRecordMutationVariables
  ) => Promise<FetchResult<MedicationStatementRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostMedicationStatementRecordMutation();
  const loading = baseLoading;

  async function createMedication(
    params: PostMedicationStatementRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postMedicationStatementRecord) {
          return;
        }
        const storeObject =
          response.postMedicationStatementRecord as unknown as StoreObject;
        try {
          cache.modify<{ listMedicationStatements: CachedRecordList }>({
            fields: {
              listMedicationStatements: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === params.input.subject.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      },
    });
    const createdMedication = response.data?.postMedicationStatementRecord;
    return {
      ...response,
      data: createdMedication
        ? new MedicationStatementRecord(createdMedication)
        : undefined,
    };
  }
  return [createMedication, loading];
}

export function useUpdateMedicationStatement(): [
  (
    params: UpdateMedicationStatementRecordMutationVariables
  ) => Promise<FetchResult<MedicationStatementRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateMedicationStatementRecordMutation();
  const loading = baseLoading;

  async function updateMedication(
    params: UpdateMedicationStatementRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedMed = response.data?.updateMedicationStatementRecord;
    return {
      ...response,
      data: updatedMed ? new MedicationStatementRecord(updatedMed) : undefined,
    };
  }
  return [updateMedication, loading];
}

export function useDeleteMedicationStatement(): [
  (
    params: DeleteMedicationStatementRecordMutationVariables
  ) => Promise<FetchResult<DeleteMedicationStatementRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteMedicationStatementRecordMutation();
  const loading = baseLoading;

  async function deleteMedicationStatement(
    params: DeleteMedicationStatementRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteMedicationStatementRecord) {
          return;
        }
        const { medication_statement_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'MedicationStatementModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteMedicationStatement, loading];
}
